import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import { NgModule } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BrowserModule } from '@angular/platform-browser';
import { LoyaltyProgramModule } from '@features/loyalty-program/loyalty-program.module';
import { MembershipModule } from '@features/membership/membership.module';
import { PointsAccountModule } from '@features/points-account/points-account.module';
import { PointsSummaryModule } from '@features/points-summary/points-summary.module';
import { RewardsPreferenceModule } from '@features/rewards-preference/rewards-preference.module';
import { TransferConnectV2SettingService } from '@features/transfer-connect-v2/services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { UserPreferenceHeaderInterceptor } from '@features/user-preference/interceptors/user-preference-header/user-preference-header.interceptor';
import { provideUnleashProxy } from '@karelics/angular-unleash-proxy-client';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { DayJsConfig } from '@rd-lib/dayjs.config';
import { RdLibModule } from '@rd-lib/rd-lib.module';
import { RdLibModuleHermesModeConfig } from '@rd-lib/rd-lib.module.config';
import { RD_UNLEASH_CONFIG } from '@rd-lib/unleash-configs';
import { MainContainerComponent } from '@rd/core/containers/main-container/main-container.component';
import { NydusNetworkAuthHeaderInterceptor } from '@rd/core/interceptors/nydus-network-auth-header/nydus-network-auth-header.interceptor';
import { AssetService } from '@rd/core/services/asset-service/asset.service';
import { AssetServiceModule } from '@rd/core/services/asset-service/asset.service.module';
import { CustomTranslateHttpLoader } from '@rd/core/services/asset-service/custom-translate-loader';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { EXTERNAL_LINK_ICON } from '@rd/core/tokens/link.token';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { PopupSettingService } from '@rd/popup/services/popup-setting/popup-setting.service';
import { RdAlertModule } from '@rd/shared-legacy/components/alert/alert.module';
import { ButtonModule } from '@rd/shared-legacy/components/button/button.module';
import { IconModule } from '@rd/shared-legacy/components/icon/icon.module';
import { LinkModule } from '@rd/shared-legacy/components/link/link.module';
import { ClickOutsideModule } from '@rd/shared-legacy/directives/click-outside/click-outside.module';
import { OpenInNewTabModule } from '@rd/shared-legacy/directives/open-in-new-tab/open-in-new-tab.module';
import { LocalizePointsNumberPipeModule } from '@rd/shared-legacy/pipes/localize-points-number/localize-points-number.pipe';
import { BannerImageModule } from '@rd/shared-legacy/widgets/banner-image/banner-image.module';
import dayjs from 'dayjs';
import dayjsLocaleEnGB from 'dayjs/locale/en-gb';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { externalModules } from '../build-specifics';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { StarAppComponent } from './app.component';
import { assetMap } from './asset-map';
import { StarAuthSecureRouteDialogComponent } from './core/components/auth-secure-route-dialog/auth-secure-route-dialog.component';
import { StarBannerComponent } from './core/components/banner/banner.component';
import { StarBypassBlockComponent } from './core/components/bypass-block/bypass-block.component';
import { StarFooterComponent } from './core/components/footer/footer.component';
import { StarHeaderProfileComponent } from './core/components/header-profile/header-profile.component';
import { StarSideNavComponent } from './core/components/side-nav/side-nav.component';
import { StarHeaderComponent } from './core/containers/header/header.component';
import { StarMobileHeaderComponent } from './core/containers/mobile-header/mobile-header.component';
import { BenefitPromptGuard } from './core/guards/benefit-prompt.guard.service';
import { StarLoyaltyCurrencyService } from './core/services/loyalty-currency/loyalty-currency.service';
import { StarPopupSettingService } from './core/services/popup-setting/popup-setting.service';
import { StarHomePageComponent } from './core/views/home-page/home-page.component';
import { ApplicationEffects } from './effects/application.effects';
import { SelectCarrierEffects } from './effects/select-carrier.effects';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { StarTransferConnectV2SettingService } from './services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';

registerLocaleData(localeEn, 'en-GB');
dayjs.extend(relativeTime, DayJsConfig.relativeTimeConfig);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.updateLocale('en-gb', {
  ...dayjsLocaleEnGB,
  relativeTime: {
    ...dayjsLocaleEnGB.relativeTime,
    d: '1 business day',
    dd: '%d business days',
  },
});

export function HttpLoaderFactory(
  http: HttpClient,
  assetService: AssetService,
): CustomTranslateHttpLoader {
  return new CustomTranslateHttpLoader(
    http,
    assetService,
    `${environment.translationBaseUrl}/`,
    '.json',
  );
}

const declaredComponents: any[] = [
  StarAppComponent,
  StarHeaderComponent,
  StarBannerComponent,
  StarFooterComponent,
  StarHeaderProfileComponent,
  StarHomePageComponent,
  StarMobileHeaderComponent,
  StarSideNavComponent,
  StarBypassBlockComponent,
  StarAuthSecureRouteDialogComponent,
  PageNotFoundComponent,
];

const supportedLocales: string[] = ['en-GB'];

@NgModule({
  declarations: [...declaredComponents],
  imports: [
    BannerImageModule,
    ClickOutsideModule,
    CommonModule,
    ButtonModule,
    IconModule,
    LinkModule,
    LocalizePointsNumberPipeModule,
    PointsSummaryModule,
    PointsAccountModule,
    MainContainerComponent,
    MembershipModule,
    BrowserModule,
    MatSidenavModule,
    LoyaltyProgramModule,
    RewardsPreferenceModule,
    OpenInNewTabModule,
    RdAlertModule,
    RdLibModule.forRoot({
      defaultCurrencyCode: 'AUD',
      defaultLocaleCode: 'en-GB',
      tenantTncsUrl: environment.tenantTncsUrl,
      tenantPrivacyStatementUrl: environment.tenantPrivacyStatementUrl,
      tenantPrivacyPolicyUrl: environment.tenantPrivacyPolicyUrl,
      nydusNetworkApiBaseUrl: environment.nydusNetworkApiBaseUrl,
      env: environment.env,
      production: environment.production,
      guardhouseApiUrl: environment.guardhouseApiUrl,
      clientId: environment.clientId,
      connection: environment.connection,
      scope: environment.scope,
      redirectUri: environment.redirectUri,
      travelEdgeBaseUrl: environment.travelEdgeBaseUrl,
      complimentaryNightBaseUrl: null,
      brandName: environment.brandName,
      portalName: 'Star Alliance Rewards',
      shortBrandName: environment.shortBrandName,
      tenantName: 'star',
      hermesMode: environment.hermesMode as RdLibModuleHermesModeConfig,
      idleCheckConfig: environment.idleCheckConfig,
      sessionCheckPollInterval: environment.sessionCheckPollInterval,
      cookiesConfig: environment.cookiesConfig,
      supportedLocales,
      pointsTransactionEnabled: true,
      prismicConfig: environment.prismicConfig,
      appId: environment.appId,
      useCustomPageTitle: true,
      assetMap,
    }),
    AssetServiceModule,
    AppRoutingModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([SelectCarrierEffects, ApplicationEffects]),
    externalModules,
  ],
  providers: [
    BenefitPromptGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NydusNetworkAuthHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserPreferenceHeaderInterceptor,
      multi: true,
    },
    { provide: PopupSettingService, useClass: StarPopupSettingService },
    { provide: LoyaltyCurrencyService, useClass: StarLoyaltyCurrencyService },
    {
      provide: TransferConnectV2SettingService,
      useClass: StarTransferConnectV2SettingService,
    },
    {
      provide: CORE_AUTH_SECURE_ROUTE_DIALOG,
      useValue: StarAuthSecureRouteDialogComponent,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        hideRequiredMarker: true,
      },
    },
    provideUnleashProxy({
      ...RD_UNLEASH_CONFIG,
      appName: environment.unleashAppName,
      clientKey: environment.unleashClientKey,
    }),
    {
      provide: EXTERNAL_LINK_ICON,
      useValue: 'icon-read-more',
    },
  ],
  bootstrap: [StarAppComponent],
})
export class AppModule {}
